import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, InputNumber } from 'antd';
import React, { useEffect, useRef, useState } from 'react';

interface QuantitySelectorProps {
    quantities: number[];
    disabled?: boolean;
    onChange: (quantities: number[]) => void;
}

/** How long to wait after the last spinner click/keystroke before committing. */
const EDIT_DEBOUNCE_MS = 700;

/**
 * Quantity chips with an explicit add flow. Keeping quantity changes in one
 * callback lets the parent preserve the existing variation fan-out behavior.
 *
 * Each chip is a real number input (spinner arrows included) rather than
 * plain text, so it reads as editable at a glance. Changing the value there
 * replaces that quantity directly — one `onChange` call, same array length —
 * instead of forcing the user to add the new quantity and then remove the
 * old one, which is the common case when the default "1 unit" chip isn't
 * what they actually want. The commit is debounced so rapid spinner clicks
 * or keystrokes only trigger a single sync once the user pauses, instead of
 * one on every click.
 */
const QuantitySelector: React.FC<QuantitySelectorProps> = ({ quantities, disabled = false, onChange }) => {
    const [isAdding, setIsAdding] = useState(false);
    const [newQuantity, setNewQuantity] = useState<number | null>(null);

    // Draft values let the user type/click freely (including transiently
    // invalid/duplicate values) while the debounce is pending.
    const [drafts, setDrafts] = useState<Record<number, number | null>>({});

    // Debounced commits can fire well after the render that scheduled them —
    // keep the latest props in refs so they never act on stale closures.
    const quantitiesRef = useRef(quantities);
    const onChangeRef = useRef(onChange);
    useEffect(() => {
        quantitiesRef.current = quantities;
        onChangeRef.current = onChange;
    }, [quantities, onChange]);

    const timersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

    useEffect(() => {
        return () => {
            Object.values(timersRef.current).forEach(clearTimeout);
        };
    }, []);

    const clearTimer = (originalQuantity: number) => {
        const timer = timersRef.current[originalQuantity];
        if (timer) {
            clearTimeout(timer);
            delete timersRef.current[originalQuantity];
        }
    };

    const closeAddPanel = () => {
        setIsAdding(false);
        setNewQuantity(null);
    };

    const addQuantity = () => {
        if (!newQuantity || newQuantity < 1 || quantities.includes(newQuantity)) {
            return;
        }

        onChange([...quantities, newQuantity]);
        closeAddPanel();
    };

    const getDraftValue = (originalQuantity: number): number | null =>
        originalQuantity in drafts ? drafts[originalQuantity] : originalQuantity;

    const commitEdit = (originalQuantity: number, draftValue: number | null) => {
        clearTimer(originalQuantity);

        setDrafts(prev => {
            const next = { ...prev };
            delete next[originalQuantity];
            return next;
        });

        const currentQuantities = quantitiesRef.current;

        // No-op edit, invalid, or a value that would collide with another
        // existing chip: silently revert to the original quantity.
        if (!draftValue || draftValue === originalQuantity || currentQuantities.includes(draftValue)) {
            return;
        }

        onChangeRef.current(currentQuantities.map(value => (value === originalQuantity ? draftValue : value)));
    };

    const handleValueChange = (originalQuantity: number, value: number | null) => {
        setDrafts(prev => ({ ...prev, [originalQuantity]: value }));

        clearTimer(originalQuantity);
        timersRef.current[originalQuantity] = setTimeout(() => {
            commitEdit(originalQuantity, value);
        }, EDIT_DEBOUNCE_MS);
    };

    return (
        <div className="quantity-selector" data-testid="quantity-selector">
            <div className="quantity-selector-chips">
                {quantities.map(quantity => (
                    <span
                        className="quantity-selector-chip"
                        data-quantity={quantity}
                        data-testid="quantity-chip"
                        key={quantity}
                    >
                        <InputNumber
                            aria-label={`Quantity (currently ${quantity} ${quantity === 1 ? 'unit' : 'units'})`}
                            className="quantity-selector-value"
                            data-testid="quantity-chip-input"
                            disabled={disabled}
                            min={1}
                            onBlur={() => commitEdit(quantity, getDraftValue(quantity))}
                            onChange={value => handleValueChange(quantity, typeof value === 'number' ? value : null)}
                            onKeyDown={event => {
                                if (event.key === 'Enter') {
                                    commitEdit(quantity, getDraftValue(quantity));
                                } else if (event.key === 'Escape') {
                                    clearTimer(quantity);
                                    setDrafts(prev => ({ ...prev, [quantity]: quantity }));
                                }
                            }}
                            precision={0}
                            size="small"
                            value={getDraftValue(quantity)}
                        />
                        <button
                            aria-label={`Remove ${quantity} ${quantity === 1 ? 'unit' : 'units'}`}
                            className="quantity-selector-remove"
                            data-quantity={quantity}
                            data-testid="quantity-chip-remove"
                            disabled={disabled || quantities.length === 1}
                            onClick={() => onChange(quantities.filter(value => value !== quantity))}
                            type="button"
                        >
                            <CloseOutlined />
                        </button>
                    </span>
                ))}
                <button
                    aria-label="Add quantity"
                    className="quantity-selector-add"
                    data-testid="quantity-add"
                    disabled={disabled}
                    onClick={() => setIsAdding(true)}
                    type="button"
                >
                    <PlusOutlined />
                    Add quantity
                </button>
            </div>

            {isAdding ? (
                <div className="quantity-selector-add-panel" data-testid="quantity-add-panel">
                    <label htmlFor="new-quantity">New quantity</label>
                    <InputNumber
                        autoFocus
                        data-testid="quantity-add-input"
                        id="new-quantity"
                        min={1}
                        onChange={value => setNewQuantity(typeof value === 'number' ? value : null)}
                        onKeyDown={event => {
                            if (event.key === 'Enter') {
                                addQuantity();
                            }
                        }}
                        placeholder="E.g. 100"
                        precision={0}
                        value={newQuantity}
                    />
                    <Button
                        data-testid="quantity-add-confirm"
                        disabled={!newQuantity || quantities.includes(newQuantity)}
                        onClick={addQuantity}
                        type="primary"
                    >
                        Add
                    </Button>
                    <Button data-testid="quantity-add-cancel" onClick={closeAddPanel}>
                        Cancel
                    </Button>
                </div>
            ) : null}
        </div>
    );
};

export default QuantitySelector;
